import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from '../../lib/util/cx';

/**
 * Port of the DS `Card`. Admin screens use `panel` (white, 12px radius,
 * hairline + card shadow, 20px padding).
 *
 * Handoff rule: cards never carry their own height — the DashboardRow does.
 * `scroll` makes the card fill its row, pin the header, and scroll the body.
 */
const SURFACE = {
  panel: 'bg-white rounded-app-card border border-panel-border shadow-card p-5',
  white: 'bg-white rounded-[22px] border border-[rgba(143,68,32,.08)] p-8',
  cream: 'bg-cream-warm rounded-[22px] border border-[rgba(143,68,32,.08)] p-8',
} as const;

export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  surface?: keyof typeof SURFACE;
  header?: ReactNode;
  actions?: ReactNode;
  scroll?: boolean;
  lift?: boolean;
}

export function Card({
  surface = 'panel',
  header,
  actions,
  scroll = false,
  lift = false,
  className,
  children,
  ...rest
}: CardProps) {
  return (
    <div
      className={cx(
        'font-body text-ink',
        SURFACE[surface],
        scroll && 'flex h-full min-h-0 w-full flex-col overflow-hidden',
        lift && 'transition-transform duration-base hover:-translate-y-1 focus-within:-translate-y-1',
        className,
      )}
      {...rest}
    >
      {(header || actions) && (
        <div className="mb-3.5 flex shrink-0 items-center justify-between gap-3">
          {typeof header === 'string' ? (
            <h4 className="m-0 font-heading text-[17px] font-bold tracking-[-.01em] text-brown-deep">{header}</h4>
          ) : (
            header
          )}
          {actions}
        </div>
      )}
      {scroll ? <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1">{children}</div> : children}
    </div>
  );
}

/** Kit `PanelTitle`: 11px uppercase eyebrow over a 19px Montserrat title, actions right-aligned. */
export function PanelTitle({
  eyebrow,
  children,
  actions,
}: {
  eyebrow?: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="mb-3.5 flex items-end justify-between gap-4">
      <div>
        {eyebrow && (
          <div className="mb-[5px] text-[11px] font-bold uppercase tracking-[.14em] text-muted">{eyebrow}</div>
        )}
        <h3 className="m-0 font-heading text-[19px] font-bold tracking-[-.01em] text-brown-deep">{children}</h3>
      </div>
      {actions}
    </div>
  );
}
